import type { Logger } from '@vp/ubik-logging'

/** Known fetch network-error messages across browsers */
const NETWORK_ERROR_MESSAGES = ['Failed to fetch', 'NetworkError when attempting to fetch resource.', 'Network request failed', 'Load failed']

/**
 * Checks whether an error is a network error from a fetch request.
 * Fetch throws a TypeError with specific messages when a genuine network failure occurs
 * (e.g., no connectivity, DNS failure, CORS pre-flight failure).
 * AbortError (DOMException) is thrown when the request is aborted due to a timeout via AbortController.
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && NETWORK_ERROR_MESSAGES.some((msg) => (error as TypeError).message.includes(msg))) {
    return true
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true
  }
  return false
}

/**
 * Retry a fetch request with exponential backoff
 * Useful for handling intermittent network failures
 *
 * @param url - The URL to fetch
 * @param options - Fetch request options
 * @param maxRetries - Maximum number of retry attempts (total attempts = maxRetries + 1)
 * @param logger - Logger instance for tracking retry attempts
 * @param context - Additional context to include in log messages
 * @returns Promise resolving to the Response object
 * @throws The last error encountered if all retry attempts fail
 */
export async function fetchWithRetry<T = unknown>(
  url: string,
  options: RequestInit,
  maxRetries: number,
  logger: Logger,
  context: Record<string, unknown>,
): Promise<T | string | null> {
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000) // Max 5s delay
        logger.info('Retrying fetch request', { url, ...context, attempt, delayMs: delay })
        await new Promise((resolve) => setTimeout(resolve, delay))
      }

      const response = await fetch(url, options)
      let data: T | string | null = null

      if (response.ok) {
        try {
          const contentLength = response.headers.get('Content-Length')
          const hasNoBody = contentLength === '0' || response.status === 204

          if (hasNoBody) {
            data = null
          } else if (response.headers.get('Content-Type')?.includes('application/json')) {
            data = await response.json()
          } else {
            data = await response.text()
          }

          return data
        } catch (error) {
          throw new Error(`Failed to parse response body: ${(error as Error).message}`)
        }
      } else {
        throw new Error(`Request to ${url} failed with status: ${response.status}`)
      }
    } catch (error) {
      lastError = error as Error

      if (lastError.message.includes('400') || lastError.message.includes('404')) {
        throw lastError
      }

      if (attempt === maxRetries) {
        logger.warn('Fetch request failed after retries', {
          url,
          ...context,
          attempts: attempt + 1,
          errorMessage: lastError.message,
          errorStack: lastError.stack,
          errorCause: lastError.cause,
          errorName: lastError.name,
        })
        throw lastError
      }
    }
  }

  throw lastError!
}
